import { captureEvent } from '../../../../../services/event-capture';

export type WeatherForecastCardName =
  | 'current conditions'
  | 'boating forecast'
  | 'swell'
  | 'wind'
  | 'tide'
  | 'rain'
  | 'temperature'
  | 'uv'
  | 'moon phase'
  | '7 day outlook'
  | '8-15 day outlook'
  | 'extended moon phases'
  | 'warnings';

/** Every collapsible weather-forecast card that must emit toggle analytics. */
export const WEATHER_FORECAST_COLLAPSIBLE_CARDS: readonly WeatherForecastCardName[] =
  [
    'current conditions',
    'boating forecast',
    'swell',
    'wind',
    'tide',
    'rain',
    'temperature',
    'uv',
    'moon phase',
    '7 day outlook',
    '8-15 day outlook',
    'extended moon phases',
    'warnings',
  ] as const;

export type WeatherForecastChartName =
  | 'swell'
  | 'wind'
  | 'tide'
  | 'rain'
  | 'temperature'
  | 'uv'
  | 'boating forecast';

export type WeatherForecastDaySelectionSource =
  | 'day strip'
  | '7 day outlook'
  | '8-15 day outlook'
  | 'moon phase'
  | 'swipe';

export type WeatherForecastDaySelectHandler = (
  date: string,
  dayLabel: string,
  source?: WeatherForecastDaySelectionSource,
) => void;

export const trackWeatherForecastCardToggled = (
  card: WeatherForecastCardName,
  expanded: boolean,
) => {
  captureEvent({
    type: 'Weather forecast: card toggled',
    payload: { card, expanded },
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastChartScrubStarted = (
  chart: WeatherForecastChartName,
) => {
  captureEvent({
    type: 'Weather forecast: chart scrub started',
    payload: { chart },
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastChartScrubEnded = (
  chart: WeatherForecastChartName,
) => {
  captureEvent({
    type: 'Weather forecast: chart scrub ended',
    payload: { chart },
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastSettingsClick = () => {
  captureEvent({
    type: 'Weather forecast: settings click',
    payload: {},
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastLocationSearchClick = () => {
  captureEvent({
    type: 'Weather forecast: location search click',
    payload: { source: 'header' },
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastLocationSearchCancelled = () => {
  captureEvent({
    type: 'Weather forecast: location search cancelled',
    payload: {},
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastLocationSelected = (payload: {
  latitude: number;
  longitude: number;
  name?: string;
  markerType?: string;
  placeId?: string;
}) => {
  captureEvent({
    type: 'Weather forecast: location selected',
    payload,
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastNearbyStationSelected = (payload: {
  chartLabel: string;
  stationType: string;
  latitude: number;
  longitude: number;
  selectedDate: string;
  stationName?: string;
}) => {
  captureEvent({
    type: 'Weather forecast: nearby station selected',
    payload,
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastDaySelected = (
  date: string,
  dayLabel: string,
  source: WeatherForecastDaySelectionSource = 'day strip',
) => {
  captureEvent({
    type: 'Weather forecast: day selected',
    payload: { date, dayLabel, source },
    sendToAmplitude: true,
  });
};

/**
 * Presence on a forecast day page — fires for the settled selected day,
 * including deep links and the default (today) page on first load.
 */
export const trackWeatherForecastDayPageViewed = (payload: {
  date: string;
  dayLabel: string;
  dayIndex: number;
}) => {
  captureEvent({
    type: 'Weather forecast: day page viewed',
    payload,
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastWarningSelected = (payload: {
  warningId: string | number;
  warningType?: string;
}) => {
  captureEvent({
    type: 'Weather forecast: warning selected',
    payload,
    sendToAmplitude: true,
  });
};

export const trackWeatherForecastWarningsExpanded = (expanded: boolean) => {
  captureEvent({
    type: 'Weather forecast: warnings expanded',
    payload: { expanded },
    sendToAmplitude: true,
  });
};
